import React, { useCallback, useMemo } from 'react'

import IconLabel from '../../icons/IconLabel'
import { faClinicMedical, faClock, faCompass, faEnvelope, faNotesMedical, faPhone, faUser } from '@fortawesome/pro-solid-svg-icons'
import { isObject } from '../../../utils'
import { getTranslate } from 'react-localize-redux'
import { connect } from 'react-redux'
import Switch from '../Switch'
import { getFullNameOrNull } from '../../../utils/entities/user'
import Italic from '../Italic'
import { List, Tooltip } from 'antd'
import { getFullAbbreviation } from '../../../utils/entities/sectionToYear'
import { isArray } from 'lodash'
import { EUROPEAN_FULL_DATE, getMoment } from '../../../utils/momentjs'
import ActsDisplayer from '../ActsDisplayer'

const { Item } = List

const mapStateToProps = state => ({ t: getTranslate(state.locale) })

const SectorIdCard = ({ restrictMode = false, sector, width, t }) => {
  const updatedAt = sector?.updatedAt ? ` (${getMoment(sector.updatedAt).format(EUROPEAN_FULL_DATE)})` : ''
  const columnMode = width < 550
  const mainClassName = 'sector-id-card-container ' + (columnMode ? 'flex-col' : 'flex-row')
  const subClassName = 'flex-col ' + (columnMode ? 'w-100' : 'w-50')

  const compositions = useMemo(() => {
    const compositions = { length: 0, parts: [] }

    if (sector?.composition) {
      Object.keys(sector.composition).forEach(k => {
        if (sector.composition[k] > 0) {
          compositions.length++
          compositions.parts.push({ value: sector.composition[k], key: k })
        }
      })
    }

    return compositions
  }, [sector?.composition])

  const renderTutors = useCallback(t => {
    const fullname = getFullNameOrNull(t)

    return (
      <Item>
        <div>
          <Switch on={fullname}> <IconLabel icon={faUser} text={<Italic text={fullname} />} /> </Switch>
          <IconLabel fontSize='small' icon={faEnvelope} text={t.email} />
          <Switch on={t.phoneNumber}> <IconLabel icon={faPhone} text={t.phoneNumber} /> </Switch>
        </div>
      </Item>
    )
  }, [])

  const renderManagers = useCallback(t => {
    const fullname = getFullNameOrNull(t)
    return (
      <Item>
        <div>
          <Switch on={fullname}> <IconLabel icon={faUser} text={<Italic text={fullname} />} /> </Switch>
          <IconLabel fontSize='small' icon={faEnvelope} text={t.email} />
        </div>
      </Item>
    )
  }, [])

  const scheduleText = useMemo(() => isArray(sector.schedules) && sector.schedules.length > 0 ? sector.schedules.map(s => t(`sector_schedules.${s.toLowerCase()}`)).join(', ') : '', [sector.schedules, t])
  const typeText = useMemo(() => sector.type ? t(`sector_types.${sector.type.toLowerCase()}`) : '', [sector.type, t])

  return (
    <div className={mainClassName}>
      <div className={subClassName}>
        <div className='service-informations-container mb-8'>
          <b> {t('sector_id_card.service_information_title')}</b>
          <Switch on={isObject(sector.institution)}> <IconLabel fontSize='small' icon={faClinicMedical} text={sector.institution.name} /> </Switch>
          <Switch on={sector.emplacement}> <IconLabel fontSize='small' icon={faCompass} text={sector.emplacement} /> </Switch>
          <Switch on={sector.phoneNumber}> <IconLabel fontSize='small' icon={faPhone} text={sector.phoneNumber} /> </Switch>
          <Switch on={sector.schedules}> <IconLabel fontSize='small' icon={faClock} text={scheduleText} /> </Switch>
          <Switch on={sector.type}> <IconLabel fontSize='small' icon={faNotesMedical} text={typeText} /> </Switch>
        </div>
        {!!sector.managers?.length && (
          <div className='service-manager-container mb-8'>
            <b> {t('sector_id_card.service_manager')}  :</b>
            <div style={{ maxHeight: '200px' }} className='overflow-y'>
              <List
                size='small'
                dataSource={sector.managers}
                renderItem={renderManagers}
              />
            </div>
          </div>
        )}
        {!!sector.tutors?.length && (
          <div className='service-tutors-container mb-8'>
            <b> {t('sector_id_card.tutors')} :</b>
            <div style={{ maxHeight: '200px' }} className='overflow-y'>
              <List
                size='small'
                dataSource={sector.tutors}
                renderItem={renderTutors}
              />
            </div>
          </div>
        )}
        {!restrictMode && (
          <div className='flex-col mb-8'>
            <b> {t('sector_id_card.last_update')} :</b>
            <span className='ft-sm'> {updatedAt} </span>
          </div>
        )}
      </div>
      <div className={subClassName}>
        <Switch on={sector.comment}>
          <div className='flex-col mb-8'>
            <b> {t('sector_id_card.comment')}</b>
            <span className='pdl-8 mh-100p overflow-y ft-sm'> {sector.comment} </span>
          </div>
        </Switch>
        <Switch on={sector.institution.globalMessage}>
          <div className='flex-col global-message-container'>
            <b> {t('sector_id_card.global_message')}</b>
            <span className='pdl-8 mh-24p overflow-y ft-sm'> {sector.institution.globalMessage} </span>
          </div>
        </Switch>
        <Switch on={!!compositions.length}>
          <div className='flex-col composition-container'>
            <b> {t('sector_id_card.composition')}</b>
            <div className='flex-row pdl-8'>
              {compositions.parts.map(part => (
                <span className='ft-sm' key={part.key} style={{ marginRight: '20px' }}> {t('composition.' + part.key)} : {part.value} </span>
              ))}
            </div>
          </div>
        </Switch>
        {isArray(sector.actTypes) && sector.actTypes.length > 0 && (
          <div className='flex-col global-message-container'>
            <b> {t('sector_id_card.internship_types')} </b>
            <ActsDisplayer actTypes={sector.actTypes} />
          </div>
        )}
        {isArray(sector.studentPathways) && sector.studentPathways.length > 0 && (
          <div className='flex-col mt-8'>
            <div className='flex-row'>
              <span className='w-33'> <b> {t('sector_id_card.student_pathway')}</b> </span>
              <span className='w-67'> <b> {t('sector_id_card.pathway_details')}</b> </span>
            </div>
            <div className='overflow-y' style={{ maxHeight: '280px' }}>
              {sector.studentPathways.map(sp => (
                <div key={'line_' + sp.id} className='flex-row'>
                  <span className='w-33 text-ellipsis ft-sm pdl-8'> {getFullAbbreviation(sp.sectionToYear)} </span>
                  <span className='w-67 text-ellipsis ft-sm'> <Tooltip title={sp.details} placement='topLeft'> {sp.details} </Tooltip> </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default connect(mapStateToProps)(SectorIdCard)
